import { z } from 'zod';
import { createArticleSchema } from '../inventory/article.schema';

/**
 * Artikel-Antrag mit Vier-Augen-Freigabe (Modul engineering, ADR 0031): ein Nutzer
 * beantragt eine neue Artikelnummer. Die **fertige Artikelnummer steht bereits bei
 * der Erstellung fest** (reserviert) — die Vier-Augen-Freigabe durch das Sekretariat
 * (engineering.approve) ist ein reiner Vollständigkeits-Check und legt anschließend
 * über die inventory-Public-API (§5) den echten Artikel an. Ablehnung legt nichts an.
 *
 * Entweder eine bestehende Zeichnungsnummer (`drawingNumberId`) ODER eine Warengruppe
 * (`commodityGroupId`, neue Zeichnungsnummer wird bei der Erstellung sofort reserviert)
 * — genau eines von beiden.
 */

/**
 * Die vollständige Artikel-Anlage als Antrags-Nutzlast — aus dem geteilten
 * `createArticleSchema` abgeleitet (omit `number` = wird generiert/reserviert,
 * `commodityGroup` = aus der Zeichnung abgeleitet). Bleibt damit automatisch
 * feldgleich zur normalen „Neuer Artikel"-Anlage.
 */
export const articleRequestPayloadSchema = createArticleSchema.omit({
  number: true,
  commodityGroup: true,
});
export type ArticleRequestPayload = z.infer<typeof articleRequestPayloadSchema>;

/**
 * Artikelart → bestehender Freitext-Tag (steuert den Beschaffungs-Vorschlag im
 * Projekt-Material-Tab, siehe `suggestSourcing()` in project-material.service.ts).
 * Der gewählte Tag wird in `articleData.tags` gemerged.
 */
export const ARTICLE_KIND_TAGS = {
  PROJECT: 'Projektbezogen',
  STANDARD: 'Standardartikel',
} as const;
export const articleKindSchema = z.enum(['PROJECT', 'STANDARD']);
export type ArticleKind = z.infer<typeof articleKindSchema>;

export const createArticleNumberRequestSchema = z
  .object({
    drawingNumberId: z.string().uuid().optional(),
    commodityGroupId: z.string().uuid().optional(),
    materialId: z.string().uuid(),
    articleKind: articleKindSchema,
    articleData: articleRequestPayloadSchema,
  })
  .refine(
    (value) => (value.drawingNumberId == null) !== (value.commodityGroupId == null),
    {
      message: 'Genau eines von Zeichnungsnummer oder Warengruppe muss angegeben werden.',
      path: ['commodityGroupId'],
    },
  );
export type CreateArticleNumberRequestInput = z.infer<typeof createArticleNumberRequestSchema>;

/**
 * Bearbeiten eines **offenen** Antrags (engineering.update, ADR 0031): nur die
 * Artikel-Details + Artikelart sind änderbar. Zeichnung/Material — und damit die
 * reservierte Artikelnummer — bleiben fest (kein Neuberechnen der Nummer, keine
 * erneute Eindeutigkeitsprüfung).
 */
export const updateArticleNumberRequestSchema = z.object({
  articleKind: articleKindSchema,
  articleData: articleRequestPayloadSchema,
});
export type UpdateArticleNumberRequestInput = z.infer<typeof updateArticleNumberRequestSchema>;

/** Ablehnung eines Antrags — Begründung ist Pflicht. */
export const rejectArticleNumberRequestSchema = z.object({
  reason: z.string().trim().min(1).max(500),
});
export type RejectArticleNumberRequestInput = z.infer<typeof rejectArticleNumberRequestSchema>;

/**
 * Rückfrage/Nachbesserung (engineering.approve): das Sekretariat gibt den Antrag mit
 * einem Pflicht-Kommentar an den Antragsteller zurück (Status RETURNED), statt ihn
 * hart abzulehnen. Der Antragsteller bessert nach und reicht erneut ein (→ PENDING).
 */
export const returnArticleNumberRequestSchema = z.object({
  comment: z.string().trim().min(1).max(500),
});
export type ReturnArticleNumberRequestInput = z.infer<typeof returnArticleNumberRequestSchema>;

export const articleNumberRequestStatusSchema = z.enum([
  'PENDING',
  'APPROVED',
  'REJECTED',
  'RETURNED',
  'WITHDRAWN',
]);
export type ArticleNumberRequestStatus = z.infer<typeof articleNumberRequestStatusSchema>;

export const articleNumberRequestResponseSchema = z.object({
  id: z.string().uuid(),
  status: articleNumberRequestStatusSchema,
  requestedBy: z.string().uuid(),
  reviewedBy: z.string().uuid().nullable(),
  reviewedAt: z.string().datetime().nullable(),
  rejectionReason: z.string().nullable(),
  drawingNumberId: z.string().uuid().nullable(),
  commodityGroupId: z.string().uuid(),
  /** Aufgelöster 4-stelliger Warengruppen-Code. */
  commodityGroupCode: z.number().int(),
  materialId: z.string().uuid(),
  materialCode: z.string(),
  /**
   * Artikelnummer XXXX.YYYYY.ZZZ — **null**, solange nicht freigegeben (ADR 0048:
   * die Nummer wird erst bei der Freigabe verbindlich vergeben). Für die Anzeige
   * einer vorläufigen Nummer offener Anträge dient `drawingNumber` + `materialCode`.
   */
  generatedNumber: z.string().nullable(),
  /** Zeichnungsnummer XXXX.YYYYY (für die vorläufige Nummern-Anzeige offener Anträge); null bei gelöschter/fehlender Zeichnung. */
  drawingNumber: z.string().nullable(),
  /** Artikelart (aus den gespeicherten Tags abgeleitet). */
  articleKind: articleKindSchema,
  /** Vollständige Artikel-Anlage (JSONB-Snapshot zur Antragszeit). */
  articleData: articleRequestPayloadSchema,
  createdArticleId: z.string().uuid().nullable(),
  createdAt: z.string().datetime(),
});
export type ArticleNumberRequestResponse = z.infer<typeof articleNumberRequestResponseSchema>;
